import SHA256 from "crypto-js/sha256";
import XTREAM from "../api/xtream";

const DEFAULT_PORTS = { http: "80", https: "443" };

// `URL` doesn't exist on the oldest TV engines, so the URL is taken apart by hand.
const URL_PARTS = /^([a-zA-Z][a-zA-Z0-9+.\-]*):\/\/([^\/?#]*)([^?#]*)(\?[^#]*)?(#.*)?$/;

/**
 * The form of a URL used only to answer "is this the same playlist?". The stored URL is never changed.
 *
 * Only what is proven not to matter is normalised: the case of the scheme and host, a default port, an empty
 * path and the fragment. User info, path (its case and trailing slash too), the query and its order are
 * compared exactly, because providers may treat any of them as significant.
 */
export const normalizeUrlForComparison = (input) => {

    const url = (input || "").trim();
    const match = URL_PARTS.exec(url);

    if (!match) return url;

    const scheme = match[1].toLowerCase();
    let authority = match[2];

    let userInfo = "";
    const at = authority.lastIndexOf("@");
    if (at !== -1) {
        userInfo = authority.slice(0, at + 1);
        authority = authority.slice(at + 1);
    }

    let host = authority;
    let port = "";

    if (authority.charAt(0) === "[") {
        const end = authority.indexOf("]");
        if (end !== -1) {
            host = authority.slice(0, end + 1);
            const rest = authority.slice(end + 1);
            port = rest.charAt(0) === ":" ? rest.slice(1) : "";
        }
    } else {
        const colon = authority.lastIndexOf(":");
        if (colon !== -1) {
            host = authority.slice(0, colon);
            port = authority.slice(colon + 1);
        }
    }

    if (port === DEFAULT_PORTS[scheme]) port = "";

    return scheme + "://" + userInfo + host.toLowerCase() + (port ? ":" + port : "") + (match[3] || "/") + (match[4] || "");

};

/**
 * What makes two playlists "the same". An Xtream account is the same however its URL is spelled (`type=`,
 * `output=`, parameter order), so it is host + username + password, the only three things the app uses.
 * Any other playlist is its URL as normalised above. http and https are different on purpose: a false
 * "duplicate" blocks a legitimate add, a missed one only costs a row.
 */
export const playlistIdentity = (url) => {

    const account = XTREAM.isXtreamPlaylist(url);

    if (account) {
        const host = normalizeUrlForComparison(account.host).replace(/\/+$/, "");
        return "xtream\n" + host + "\n" + account.username + "\n" + account.password;
    }

    return "url\n" + normalizeUrlForComparison(url);

};

// Only this hash is kept for comparing, so a password is never stored readable to make it work.
// SHA-256 in pure JS: `crypto.subtle` is missing on old engines and on non-secure origins.
export const computeFingerprint = (url) => SHA256(playlistIdentity(url)).toString();

export const randomUid = () => {

    let hex = "";

    try {
        const cryptoObj = window.crypto || null;
        if (cryptoObj && typeof cryptoObj.getRandomValues === "function") {
            const bytes = new Uint8Array(16);
            cryptoObj.getRandomValues(bytes);
            for (let i = 0; i < bytes.length; i++) hex += (bytes[i] < 16 ? "0" : "") + bytes[i].toString(16);
            return hex;
        }
    } catch (e) { }

    // not a secret, it only has to be unique
    for (let i = 0; i < 4; i++) hex += Math.floor(Math.random() * 0x100000000).toString(16);

    return hex + Date.now().toString(16);

};
